import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { css } from '@emotion/react';
import styled from '@emotion/styled';

interface ChatLeaveMessageProps {
  group: boolean;
  isOpen: boolean;
  onClose: () => void;
  handleRoomLeaveClick: (group: boolean) => void;
}

export default function ChatLeaveMessage({
  group,
  isOpen,
  onClose,
  handleRoomLeaveClick,
}: ChatLeaveMessageProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      onClose();
    };
  }, []);

  return createPortal(
    <>
      {isOpen && (
        <Container>
          <Wrapper ref={wrapperRef}>
            <TopWrapper>
              <Title>Leave</Title>
              <Description>Are you sure you want to leave?</Description>
            </TopWrapper>
            <BottomButtonWrapper>
              <button onClick={onClose}>Cancel</button>
              <button
                onClick={() => handleRoomLeaveClick(group)}
                className='on'
              >
                Leave
              </button>
            </BottomButtonWrapper>
          </Wrapper>
        </Container>
      )}
    </>,
    document.body,
  );
}

const Container = styled.div`
  ${({ theme }) => css`
    display: flex;
    position: fixed;
    z-index: 500;
    top: 0;
    left: 0;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    background: ${theme.backgroundColor.bg_dark_5_alpha};
  `}
`;

const Wrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    margin: 0 auto;
    border-radius: 16px;
    background: ${theme.backgroundColor.bg_dark_3};
    gap: 24px;
  `}
`;

const TopWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 24px 24px 0 24px;
  gap: 8px;
`;

const Title = styled.div`
  ${({ theme }) => css`
    ${theme.fontStyle.text.body18_bold};
    color: ${theme.content.content_light_1};
  `}
`;

const Description = styled.div`
  ${({ theme }) => css`
    ${theme.fontStyle.text.body16_medium};
    color: ${theme.content.content_light_2};
  `}
`;

const BottomButtonWrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    width: 100%;

    button {
      display: flex;
      flex: 1 0 0;
      align-items: center;
      align-self: stretch;
      justify-content: center;
      width: 100%;
      height: 56px;
      border-top: 1px solid ${theme.stroke.stroke_1};
      border-right: 1px solid ${theme.stroke.stroke_1};
      color: ${theme.content.content_light_1};
      ${theme.fontStyle.text.body16_medium};

      &.on {
        color: ${theme.themeColor.negative};
      }

      &:last-child {
        border-right: 1px solid transparent;
      }
    }
  `}
`;
